import { useEffect, useState } from "react";

// ===== RESPONSIVE HELPER FUNCTIONS =====
// Get responsive breakpoints
export const getScreenSize = () => {
  const width = window.innerWidth;
  if (width <= 480) return "xs"; // Extra small mobile
  if (width <= 768) return "sm"; // Small mobile/large mobile
  if (width <= 1024) return "md"; // Tablet
  if (width <= 1366) return "lg"; // Small laptop
  if (width <= 1920) return "xl"; // Large laptop
  return "xxl"; // Desktop
};

// Get responsive spacing based on screen size
export const getResponsiveSpacing = (screenSize) => {
  const spacingMap = {
    xs: { padding: "0.5rem", margin: "0.25rem", gap: "0.5rem" },
    sm: { padding: "0.75rem", margin: "0.5rem", gap: "0.75rem" },
    md: { padding: "1rem", margin: "0.75rem", gap: "1rem" },
    lg: { padding: "1.25rem", margin: "1rem", gap: "1.25rem" },
    xl: { padding: "1.5rem", margin: "1.25rem", gap: "1.5rem" },
    xxl: { padding: "2rem", margin: "1.5rem", gap: "2rem" },
  };
  return spacingMap[screenSize] || spacingMap.lg;
};

// Get responsive font sizes
export const getResponsiveFontSizes = (screenSize) => {
  const fontMap = {
    xs: {
      title: "1.25rem",
      subtitle: "0.875rem",
      body: "0.75rem",
      small: "0.6875rem",
      tiny: "0.625rem",
    },
    sm: {
      title: "1.5rem",
      subtitle: "1rem",
      body: "0.875rem",
      small: "0.75rem",
      tiny: "0.6875rem",
    },
    md: {
      title: "1.75rem",
      subtitle: "1.125rem",
      body: "0.9375rem",
      small: "0.8125rem",
      tiny: "0.75rem",
    },
    lg: {
      title: "2rem",
      subtitle: "1.25rem",
      body: "1rem",
      small: "0.875rem",
      tiny: "0.8125rem",
    },
    xl: {
      title: "2.25rem",
      subtitle: "1.375rem",
      body: "1rem",
      small: "0.875rem",
      tiny: "0.8125rem",
    },
    xxl: {
      title: "2.5rem",
      subtitle: "1.5rem",
      body: "1.125rem",
      small: "1rem",
      tiny: "0.875rem",
    },
  };
  return fontMap[screenSize] || fontMap.lg;
};

// Get responsive grid columns
export const getResponsiveGrid = (screenSize) => {
  const gridMap = {
    xs: 1,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 5,
    xxl: 6,
  };
  return gridMap[screenSize] || 4;
};

// ===== RESPONSIVE STATE MANAGEMENT =====
export const useResponsive = () => {
  const [screenSize, setScreenSize] = useState(getScreenSize());
  const [spacing, setSpacing] = useState(getResponsiveSpacing(screenSize));
  const [fonts, setFonts] = useState(getResponsiveFontSizes(screenSize));
  const [gridCols, setGridCols] = useState(getResponsiveGrid(screenSize));

  // Update responsive values on window resize
  useEffect(() => {
    const handleResize = () => {
      const newScreenSize = getScreenSize();
      setScreenSize(newScreenSize);
      setSpacing(getResponsiveSpacing(newScreenSize));
      setFonts(getResponsiveFontSizes(newScreenSize));
      setGridCols(getResponsiveGrid(newScreenSize));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return { screenSize, spacing, fonts, gridCols };
};
